import { createModel } from '@rematch/core';

import { toast } from 'react-toastify';
import { toastErrorDeduped } from '../../utils/toast-dedupe';

// Constants
import URL from '../../constants/api-constants';
import { MAX_DATA_RETENTION_HOURS } from '../../constants/common';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';
import { createPath, generatePath } from 'react-router-dom';
import { TimeInterval } from '../../types/time-interval';
import { Location } from './locations';
import { Contract } from './contracts';
import { SearchCycle } from '../../types/search-name';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { CycleController } from './controllers';
import SortType from '../../enums/sort-type.enum';
import CycleStatus from '../../enums/cycle-status.enum';
import CycleSettings from '../../enums/cycle-setting.enum';
import { IValueWithTime } from '../../types/channel-types';
import {
  IPostWeldSettings,
  ModeSettings,
  PercentageTimerSettings,
  PreHeatSettings,
} from '../../types/cycle-settings-types';
import { CycleSettingAction } from '../../enums/action';
import { ChartPreference } from './chart-preference';
import { Screen } from './screens';
import { CycleIotDataReader } from '../../utils/cycle-utils';
import { IControllerSettings } from '../../types/controller-types';
import {
  IColorsDevices,
  IExcludedChannelNames,
  IUpdateExclusionData,
  TUpdateColorData,
} from '../../components/ReportChannelsModal/ReportChannelsModal';
import { ScreenService } from '../http/screen.service';
import { CycleDataService } from '../http/cycle-data.service';
import { CyclesCountResponse } from '../../types/cycle';
import { ContractService } from '../http/contract.service';
import { LocationService } from '../http/location.service';
import ContractStatus from '../../enums/status-contracts-location.enum';
import { CycleItem } from '../../components/Cycle/types';
import { IRecipeStepEditable } from '../../types/recipes-types';
import { ICycleIoTChannelsSettings } from './types/cycle/ICycleIoTChannelsSettings';

export type IoTDataResponse = {
  thermocouple?: string;
  ser?: string;
  pk?: string;
  sk?: number;
  channel?: string;
  createdAt?: Date;
  mlis?: string;
  mode?: string;
  oc?: string;
  po?: number;
  serial?: string;
  sp?: number;
  state?: number;
  stcn?: string;
  temp?: number;
  manualFault?: {
    message?: string;
    faultDatetime?: number;
  } | null;
};

type NoteCoordinate = {
  x: number;
  y: number;
};

export type NoteLink = {
  id: string;
  coordinate: NoteCoordinate;
  memberId?: string;
  noteId?: string;
  created_at?: string;
  updated_at?: string;
};

export type Note = {
  id: string;
  name: string;
  coordinate: NoteCoordinate;
  memberId?: string;
  cycleId?: string;
  created_at?: string;
  updated_at?: string;
  links?: NoteLink[];
};

export type MetaField = {
  key: string;
  value: string;
};

export type CycleReports = {
  id: string;
  created_at: string;
  updated_at: string;
  isActive: boolean;
  cycleId: string;
};

export type Cycle = {
  id: string;
  name: string;
  report_id?: string;
  start_at: string | null;
  end_at: string | null;
  created_at?: string;
  updated_at?: string;
  cycle_controllers?: CycleController[];
  notes?: Note[];
  //Need to separate return data
  iotDataResponse?: { [key: string]: IoTDataResponse[] } | IoTDataResponse[];
  location?: Location;
  contract?: Contract;
  status?: CycleStatus;
  chart_preferences?: ChartPreference[];
  settingsMode?: IValueWithTime<CycleSettings>;
  settings?: PreHeatSettings | PercentageTimerSettings | IPostWeldSettings;
  locationId: string;
  contractId: string;
  screens?: Screen[];
  controllersSettings: Record<string, IControllerSettings>;
  maxSp: number;
  cycle_reports: CycleReports[];
  autoSPAdjust: boolean;
  autoPause: boolean;
  autoResetTimers: boolean;
  packages?: CyclePackage[];
  deletedAt?: string | null;
};

export type ActiveCycleChannelConflict = {
  channelItemId: string;
  controllerId: string;
  controllerName: string;
  controllerSerialNumber: string;
  channelNumber: number;
  activeCycleIds: string[];
  activeCycleNames: string[];
  activeCycleContractNames: (string | null)[];
};

export type CycleAttachment = {
  id: string;
  attachment: string;
  created_at?: string;
  updated_at?: string;
  cycleId: string;
  name: string;
};

export type CyclePackage = {
  id: string;
  file: string;
  cycleId: string;
  name: string;
  memberId: string | null;
  creator: string | null;
};

const DEFAULT_STATE: {
  cycles: Cycle[];
  cycle: Cycle | null;
  attachments: CycleAttachment[];
  isViewSettings: boolean;
  packages: CyclePackage[] | null;
  activeScreensCycles: Record<string, Cycle[]>;
  activeScreenlessCycles: Cycle[];
  inactiveCycles: Cycle[];
  completedCycles: Cycle[];
  deletedCycles: Cycle[];
  cyclesCount: CyclesCountResponse | null;
  searchCycleQuery: string;
  cycleDbSettings: IDbCycleSettings | null;
  cycleIoTChannelsSettings: ICycleIoTChannelsSettings | null;
  iotDataLoadEnds: boolean;
  colorDevices: IColorsDevices[];
  excludedChannelsNames: IExcludedChannelNames[];
  firstLoadComplete: boolean;
} = {
  cycles: [],
  cycle: null,
  attachments: [],
  isViewSettings: false,
  packages: null,
  activeScreensCycles: {},
  activeScreenlessCycles: [],
  inactiveCycles: [],
  completedCycles: [],
  deletedCycles: [],
  cyclesCount: null,
  searchCycleQuery: '',
  cycleDbSettings: null,
  cycleIoTChannelsSettings: null,
  iotDataLoadEnds: false,
  colorDevices: [],
  excludedChannelsNames: [],
  firstLoadComplete: false,
};

type TUpdateExcludeChannelNames = {
  cycleId: string;
  data: IUpdateExclusionData[];
};

type TUpdateColorDevices = {
  cycleId: string;
  data: TUpdateColorData[];
};

export interface IDbCycleSettings {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
  mode: CycleSettings;
  rateUpperTolerance: number;
  rateLowerTolerance: number;
  holdUpperTolerance: number;
  holdLowerTolerance: number;
  rate: number;
  finalTemp: number;
  soakUpper: number;
  soakLower: number;
  percentageTimer: number;
  organizationId: string | null;
  cycleId: string;
  steps: IRecipeStepEditable[] | [];
  pendingCycleSettings?: IDbCycleSettings | null;
}
const raiseCondtitionCheck = {
  colorDevices: 0,
  excludeChannelNames: 0,
  cycleLogsStream: 0,
};

const streamLogsChecker = (reader: ReadableStreamDefaultReader, requestNum: number) => {
  if (requestNum !== raiseCondtitionCheck.cycleLogsStream) {
    reader.cancel();
  }
};

export const cycles = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    set(state, payload) {
      return {
        ...state,
        ...payload,
      };
    },
    setOneData(state, payload) {
      return {
        ...state,
        cycle: {
          ...state.cycle,
          ...payload,
        },
      };
    },
    setSettingsMode(state, payload) {
      return {
        ...state,
        cycle: {
          ...state.cycle,
          settingsMode: payload,
        },
      };
    },
    setSettings(state, payload) {
      return {
        ...state,
        cycle: {
          ...state.cycle,
          settings: payload,
        },
      };
    },
    setDbSettings(state, payload) {
      return {
        ...state,
        cycleDbSettings: payload,
      };
    },
    setCycleIoTChannelsSettings(state, payload) {
      return {
        ...state,
        cycleIoTChannelsSettings: payload,
      };
    },
    setCycle(state, payload: Cycle) {
      const cycle = state.cycle;
      const iotData: Record<string, any[]> = {};

      if (cycle?.iotDataResponse) {
        Object.keys(cycle.iotDataResponse).forEach((key) => {
          iotData[key] = cycle.iotDataResponse[key];
        });
      }

      if (payload?.iotDataResponse) {
        Object.keys(payload.iotDataResponse).forEach((key) => {
          if (iotData[key]) {
            iotData[key].push(...payload[key]);
          } else {
            iotData[key] = payload[key];
          }
        });
      }

      return {
        ...state,
        cycle: {
          ...cycle,
          ...payload,
          settings: payload.settings ?? cycle?.settings,
          settingsMode: payload.settingsMode ?? cycle?.settingsMode,
          iotDataResponse: { ...iotData },
        },
      };
    },

    setIsViewSettings(state, payload: boolean) {
      return { ...state, isViewSettings: payload };
    },

    setIotDataLoadEnds(state, payload: boolean) {
      return { ...state, iotDataLoadEnds: payload };
    },

    setFirstLoadComplete(state, payload: boolean) {
      return { ...state, firstLoadComplete: payload };
    },

    setCycleStreamIotData(
      state,
      payload: Record<string, any[]> | { data: Record<string, any[]>; skipRetention?: boolean },
    ) {
      const cycle = state.cycle;
      const iotData: Record<string, any[]> = {};

      const data = 'data' in payload ? payload.data : payload;
      const skipRetention = 'skipRetention' in payload ? payload.skipRetention : false;

      if (cycle.iotDataResponse) {
        Object.keys(cycle.iotDataResponse).forEach((key) => {
          iotData[key] = cycle.iotDataResponse[key];
        });
      }

      let maxSp = state.cycle.maxSp || 0;
      const payloadKeys = Object.keys(data);

      const findFirstIndex = (arr: any[], cutoff: number): number => {
        let left = 0;
        let right = arr.length - 1;
        let result = arr.length;
        while (left <= right) {
          const mid = Math.floor((left + right) / 2);
          if (arr[mid].sk >= cutoff) {
            result = mid;
            right = mid - 1;
          } else {
            left = mid + 1;
          }
        }
        return result;
      };

      for (const key of payloadKeys) {
        if (!iotData[key]) {
          iotData[key] = [];
        }

        let currentMaxSp = data[key][0]?.sp;

        const isOlderData = iotData[key].length > 0 && data[key].length > 0 && data[key][0].sk < iotData[key][0].sk;

        for (const dataPoint of data[key]) {
          const currentSp = dataPoint?.sp;
          if (currentSp > currentMaxSp) {
            currentMaxSp = currentSp;
          }
        }

        if (isOlderData) {
          iotData[key] = data[key].concat(iotData[key]);
        } else {
          iotData[key] = iotData[key].concat(data[key]);
        }

        if (!skipRetention) {
          const cutoffTime = Date.now() - MAX_DATA_RETENTION_HOURS * 60 * 60 * 1000;
          const startIndex = findFirstIndex(iotData[key], cutoffTime);
          iotData[key] = iotData[key].slice(startIndex);
        }

        maxSp = maxSp < currentMaxSp ? currentMaxSp : maxSp;
      }

      return {
        ...state,
        cycle: { ...cycle, iotDataResponse: { ...iotData }, maxSp },
      };
    },
    setList(state, payload) {
      return {
        ...state,
        cycles: payload,
      };
    },
    setChartPreference(state, payload) {
      return {
        ...state,
        cycle: {
          ...state.cycle,
          chart_preference: payload,
        },
      };
    },
    resetOne(state) {
      return {
        ...state,
        cycle: DEFAULT_STATE.cycle,
      };
    },
    resetList(state) {
      return {
        ...state,
        cycles: DEFAULT_STATE.cycles,
      };
    },
    resetState() {
      return DEFAULT_STATE;
    },
    clearDbSettings(state) {
      return {
        ...state,
        cycleDbSettings: null,
      };
    },
    clearCycleIoTChannelsSettings(state) {
      return {
        ...state,
        cycleIoTChannelsSettings: null,
      };
    },
    clearSettings(state) {
      return {
        ...state,
        cycle: {
          ...state.cycle,
          settings: null,
        },
      };
    },
    clearSettingsMode(state) {
      return {
        ...state,
        cycle: {
          ...state.cycle,
          settingsMode: null,
        },
      };
    },
    setAttachments(state, payload: CycleAttachment[]) {
      return { ...state, attachments: payload };
    },
    resetAttachments(state) {
      return { ...state, attachments: DEFAULT_STATE.attachments };
    },
    setCyclePackage(state, payload: CyclePackage[]) {
      return {
        ...state,
        packages: payload,
      };
    },
    setCyclesCount(state, payload: CyclesCountResponse) {
      return { ...state, cyclesCount: { ...payload } };
    },
    resetCyclesCount(state) {
      return { ...state, cyclesCount: DEFAULT_STATE.cyclesCount };
    },

    setScreenCycles(state, payload: { screenId: string; cycles: Cycle[] }) {
      const currentScreenCycles = { ...state.activeScreensCycles };
      currentScreenCycles[payload.screenId] = payload.cycles;

      return { ...state, activeScreensCycles: { ...currentScreenCycles } };
    },
    resetScreenCycles(state) {
      return { ...state, activeScreensCycles: DEFAULT_STATE.activeScreensCycles };
    },

    setScreenlessCycles(state, payload: { cycles: Cycle[] }) {
      return { ...state, activeScreenlessCycles: payload.cycles };
    },
    resetScreenlessCycles(state) {
      return { ...state, activeScreenlessCycles: DEFAULT_STATE.activeScreenlessCycles };
    },

    setInactiveCycles(state, payload: { cycles: Cycle[] }) {
      return { ...state, inactiveCycles: payload.cycles };
    },
    resetInactiveCycles(state) {
      return { ...state, inactiveCycles: DEFAULT_STATE.inactiveCycles };
    },

    setDeletedCycles(state, payload: { cycles: Cycle[] }) {
      return { ...state, deletedCycles: payload.cycles };
    },
    resetDeletedCycles(state) {
      return { ...state, deletedCycles: DEFAULT_STATE.deletedCycles };
    },

    setCompletedCycles(state, payload: { cycles: Cycle[] }) {
      return { ...state, completedCycles: payload.cycles };
    },
    resetCompletedCycles(state) {
      return { ...state, completedCycles: DEFAULT_STATE.completedCycles };
    },

    setSearchCycleQuery(state, payload: string) {
      return { ...state, searchCycleQuery: payload };
    },
    resetSearchCycleQuery(state) {
      return { ...state, searchCycleQuery: DEFAULT_STATE.searchCycleQuery };
    },
    resetCyclePackage(state) {
      return {
        ...state,
        cyclePackage: null,
      };
    },
    setColorDevices(state, payload: IColorsDevices[]) {
      return { ...state, colorDevices: payload };
    },
    resetColorDevices(state) {
      return { ...state, colorDevices: DEFAULT_STATE.colorDevices };
    },

    setExcludedChannelsNames(state, payload: IExcludedChannelNames[]) {
      return { ...state, excludedChannelsNames: payload };
    },
    resetExcludedChannelsNames(state) {
      return { ...state, excludedChannelsNames: DEFAULT_STATE.excludedChannelsNames };
    },
  },
  effects: (dispatch) => {
    return {
      async createCycle(payload) {
        try {
          const response = await http.post(URL.CYCLES, payload);
          toast.success('Cycle created successfully');
          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async duplicateCycle(data: { id: string; count: number }) {
        try {
          const path = generatePath(URL.CYCLE_DUPLICATE, { id: data.id });
          const response = await http.post(path, { count: data.count });
          toast.success(`${data.count} duplicate${data.count > 1 ? 's' : ''} created successfully`);
          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async updateCycle(data) {
        try {
          const { id, ...payload } = data;
          const path = generatePath(URL.CYCLE, { id });
          const response = await http.patch(path, payload);
          dispatch.cycles.set({ cycle: response.data });
          toast.success('Cycle updated successfully');

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async deleteScreenFromCycle(data: { screenId: string; cycleId: string }) {
        try {
          const { cycleId, screenId } = data;
          const path = generatePath(URL.CYCLE_SCREEN, { id: cycleId });
          const response = await http.delete(path, { data: { screenId } });
          dispatch.cycles.set({ cycle: response.data });
          toast.success('Cycle updated successfully');

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getCycles(data: { sort: SortType }): Promise<Cycle[]> {
        try {
          const searchParams = new URLSearchParams({ sort: data.sort });

          const fullPath = createPath({
            pathname: URL.CYCLES,
            search: `?${searchParams}`,
          });

          const response = await http.get(fullPath);
          dispatch.cycles.set({ cycles: response.data });

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getCyclesCount(contractId: string): Promise<CyclesCountResponse> {
        try {
          const data = await ContractService.getCyclesCount(contractId);

          dispatch.cycles.setCyclesCount(data);

          return data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getCyclesCountByLocation(payload: {
        locationId: string;
        query?: { contractStatus: ContractStatus };
      }): Promise<CyclesCountResponse> {
        try {
          const data = await LocationService.getCyclesCount(payload);

          dispatch.cycles.setCyclesCount(data);

          return data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toastErrorDeduped(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toastErrorDeduped(error.message);
          } else {
            toastErrorDeduped('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getScreenCycles(screenId: string): Promise<Cycle[]> {
        try {
          const data = await ScreenService.getAllCyclesByScreen({ screenId });

          dispatch.cycles.setScreenCycles({ screenId, cycles: data });

          return data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getScreenlessCycles(contractId: string): Promise<Cycle[]> {
        try {
          const data = await CycleDataService.getActiveWithoutScreen(contractId);

          dispatch.cycles.setScreenlessCycles({ cycles: data });

          return data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getScreenlessCyclesByLocation(locationId: string): Promise<Cycle[]> {
        try {
          const data = await CycleDataService.getActiveWithoutScreenByLocation(locationId);

          dispatch.cycles.setScreenlessCycles({ cycles: data });

          return data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getCompletedCycles(contractId: string): Promise<Cycle[]> {
        try {
          const data = await CycleDataService.getCompleted(contractId);

          dispatch.cycles.setCompletedCycles({ cycles: data });

          return data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getCompletedCyclesByLocation(payload: {
        locationId: string;
        query?: { contractStatus?: ContractStatus };
      }): Promise<Cycle[]> {
        try {
          const { locationId } = payload;
          const contractStatus = payload?.query?.contractStatus || ContractStatus.OPEN;
          const data = await CycleDataService.getCompletedByLocation({
            locationId,
            query: { contractStatus },
          });

          dispatch.cycles.setCompletedCycles({ cycles: data });

          return data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toastErrorDeduped(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toastErrorDeduped(error.message);
          } else {
            toastErrorDeduped('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getInactiveCycles(contractId: string): Promise<Cycle[]> {
        try {
          const data = await CycleDataService.getInactive(contractId);

          dispatch.cycles.setInactiveCycles({ cycles: data });

          return data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getInactiveCyclesByLocation(payload: {
        locationId: string;
        query?: { contractStatus?: ContractStatus };
      }): Promise<Cycle[]> {
        try {
          const { locationId } = payload;
          const contractStatus = payload?.query?.contractStatus || ContractStatus.OPEN;

          const data = await CycleDataService.getInactiveByLocation({
            locationId,
            query: { contractStatus },
          });

          dispatch.cycles.setInactiveCycles({ cycles: data });

          return data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toastErrorDeduped(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toastErrorDeduped(error.message);
          } else {
            toastErrorDeduped('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getDeletedCyclesByLocation(locationId: string): Promise<Cycle[]> {
        try {
          const data = await CycleDataService.getDeletedByLocation(locationId);

          dispatch.cycles.setDeletedCycles({ cycles: data });

          return data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toastErrorDeduped(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toastErrorDeduped(error.message);
          } else {
            toastErrorDeduped('An unexpected error occurred');
          }
          throw error;
        }
      },

      async restoreCycle(payload: { id: string; status?: CycleStatus }, state): Promise<void> {
        try {
          const { id, status } = payload;
          const path = generatePath(URL.CYCLE_RESTORE, { id });
          await http.patch(path, status ? { status } : {});

          const filteredDeletedCycles = state.cycles.deletedCycles.filter((cycle) => cycle.id !== id);
          dispatch.cycles.setDeletedCycles({ cycles: filteredDeletedCycles });
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getCycleChannelConflicts(id: string): Promise<ActiveCycleChannelConflict[]> {
        try {
          const path = generatePath(URL.CYCLE_CHANNEL_CONFLICTS, { id });
          const response = await http.get(path);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getListByContractAndScreen(data: {
        screenId: string;
        contractId: string;
        query: { sort: SortType; status?: CycleStatus };
      }): Promise<Cycle[]> {
        try {
          const searchParams = new URLSearchParams(data.query);

          const pathname = generatePath(URL.CYCLES_CONTRACT_SCREEN, {
            screenId: data.screenId,
            contractId: data.contractId,
          });
          const fullPath = createPath({
            pathname,
            search: `?${searchParams}`,
          });

          const response = await http.get(fullPath);
          dispatch.cycles.setList(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async deleteCycle(id, state): Promise<void> {
        try {
          const path = generatePath(URL.CYCLE, { id });
          await http.delete(path);

          const filteredCycles = state.cycles.cycles.filter((cycle) => cycle.id !== id);
          dispatch.cycles.setList(filteredCycles);
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getOne(id: string): Promise<Cycle> {
        try {
          const path = generatePath(URL.CYCLE, { id });
          const response = await http.get<Cycle>(path);

          if (response.data) {
            await dispatch.locations.getCurrentLocation(response.data.locationId);
            if (response.data.contractId) {
              await dispatch.contracts.getContract(response.data.contractId);
            }
          }

          dispatch.cycles.setCycle(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getOneWithLatestIot(id: string): Promise<CycleItem> {
        try {
          const path = generatePath(URL.CYCLE_LATEST_IOT, { id });
          const response = await http.get<CycleItem>(path);

          dispatch.cycles.setCycle(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getChartCycleData(data: {
        id: string;
        query?: TimeInterval & { bucket?: number };
        skipRetention?: boolean;
        staticFirstDataLoad?: boolean;
      }): Promise<Cycle> {
        const responseData = await dispatch.cycles.getOne(data.id);
        if (data.staticFirstDataLoad)
          dispatch.cycles.getCycleLogsStream({ ...data, skipRetention: data.skipRetention ?? false });
        else await dispatch.cycles.getCycleLogsStream({ ...data, skipRetention: data.skipRetention ?? false });

        return responseData;
      },

      async getCycleLogsStream(data: {
        id: string;
        query?: TimeInterval & { bucket?: number };
        skipRetention?: boolean;
        staticFirstDataLoad?: boolean;
      }) {
        try {
          if (data.staticFirstDataLoad) {
            dispatch.cycles.setFirstLoadComplete(false);
          }
          raiseCondtitionCheck.cycleLogsStream += 1;

          const requestNum = raiseCondtitionCheck.cycleLogsStream;

          const path = generatePath(URL.CYCLE_LOGS_STREAM, { id: data.id });

          const getOptions: AxiosRequestConfig = { responseType: 'stream', adapter: 'fetch' };

          if (data?.query) {
            const [from, to] = [data.query.from, data.query.to].sort();
            getOptions.params = { from, to };
          }

          if (data?.query?.bucket) {
            getOptions.params = { ...getOptions.params, bucket: data.query.bucket };
          }

          const response = await http.get<ReadableStream>(path, getOptions);

          const reader = response.data.getReader();
          const dataReader = new CycleIotDataReader(
            reader,
            data.staticFirstDataLoad
              ? (partialData) => {
                  if (requestNum === raiseCondtitionCheck.cycleLogsStream) {
                    dispatch.cycles.setCycleStreamIotData({ data: partialData, skipRetention: data.skipRetention });
                  }
                  streamLogsChecker(reader, requestNum);
                }
              : () => streamLogsChecker(reader, requestNum),
          );

          const streamIotData = await dataReader.getIotData();
          if (requestNum === raiseCondtitionCheck.cycleLogsStream) {
            dispatch.cycles.setCycleStreamIotData({ data: streamIotData, skipRetention: data.skipRetention });
            if (data.staticFirstDataLoad) {
              dispatch.cycles.setFirstLoadComplete(true);
            }
          }
          return streamIotData;
        } catch (error: unknown) {
          console.error(error);
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          }
        }
      },

      async getAllByContractId(data: { contractId: string; query?: SearchCycle }): Promise<Cycle[]> {
        try {
          const { contractId, query } = data;

          let path = generatePath(URL.CYCLES_CONTRACT, { contractId });

          if (query) {
            const searchParams = new URLSearchParams(query).toString();
            path = createPath({
              pathname: path,
              search: `?${searchParams}`,
            });
          }
          const response = await http.get(path);

          dispatch.cycles.setList(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getAllByLocationId(data: { locationId: string; query?: SearchCycle }): Promise<Cycle[]> {
        try {
          const { locationId, query } = data;

          let path = generatePath(URL.CYCLES_LOCATION, { locationId });

          if (query) {
            const searchParams = new URLSearchParams(query).toString();
            path = createPath({
              pathname: path,
              search: `?${searchParams}`,
            });
          }
          const response = await http.get(path);

          dispatch.cycles.setList(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getAllWithLatestIotByContractId(data: {
        contractId: string;
        query?: { status: CycleStatus };
      }): Promise<Cycle[]> {
        try {
          const { contractId, query } = data;

          const path = generatePath(URL.CYCLES_CONTRACT_IOT_LATEST, { contractId });

          const config: AxiosRequestConfig = {};

          if (query) {
            config.params = query;
          }
          const response = await http.get(path, config);

          dispatch.cycles.setList(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getSettingsMode(id: string): Promise<IValueWithTime<CycleSettings>> {
        try {
          const path = generatePath(URL.CYCLE_SETTINGS_MODE, { id });
          const response = await http.get(path);
          const mode: ModeSettings = response.data;

          dispatch.cycles.setSettingsMode(mode.mode);

          return mode.mode;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getSettingsByModeForOneChannel(data: {
        id: string;
        query: { name: CycleSettings; channelId: string; controllerId: string };
      }): Promise<PercentageTimerSettings | PreHeatSettings | IPostWeldSettings> {
        try {
          const { id, query } = data;
          const searchParams = new URLSearchParams(query).toString();
          const path = createPath({
            pathname: generatePath(URL.CYCLE_CHANNEL_SETTINGS, { id }),
            search: searchParams,
          });
          const response = await http.get(path);

          dispatch.cycles.setSettings(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getSettingsByMode(data: {
        id: string;
        query: { name: CycleSettings };
      }): Promise<PercentageTimerSettings | PreHeatSettings | IPostWeldSettings> {
        try {
          const { id, query } = data;
          const searchParams = new URLSearchParams(query).toString();
          const path = createPath({
            pathname: generatePath(URL.CYCLE_SETTINGS, { id }),
            search: `?${searchParams}`,
          });
          const response = await http.get(path);

          dispatch.cycles.setSettings(response.data);
          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getDbCycleSettings(id: string): Promise<IDbCycleSettings> {
        try {
          const path = generatePath(URL.CYCLE_DB_SETTINGS, { id });
          const response = await http.get(path);

          dispatch.cycles.setSettingsMode({ value: response.data.mode, timestamp: response.data.updated_at });
          dispatch.cycles.setDbSettings(response.data);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getPlannedRecipe(id: string): Promise<{ source: 'recipe' | 'pending' | null; settings: any }> {
        try {
          const path = generatePath(URL.CYCLE_PLANNED_RECIPE, { id });
          const response = await http.get(path);

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getIoTCycleSettings(id: string): Promise<ICycleIoTChannelsSettings> {
        try {
          const path = generatePath(URL.CYCLE_IOT_SETTINGS, { id });
          const response = await http.get(path);

          const data = {
            channels: response.data,
            lastUpdatedAt: new Date().toISOString(),
          };

          dispatch.cycles.setCycleIoTChannelsSettings(data);

          return data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async start({ id, skipCalCertCheck }: { id: string; skipCalCertCheck?: boolean }) {
        try {
          const path = generatePath(URL.CYCLE_START, { id });
          await http.post(path, { skipCalCertCheck });
          toast.success('Cycle started successfully');
        } catch (error: unknown) {
          const isMissingCalcertError =
            error instanceof AxiosError && error.response?.data?.code === 'CONTROLLERS_MISSING_VALID_CALCERT';

          if (isMissingCalcertError) {
            // Surfaced via a dedicated popup instead of the generic toast below.
          } else if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async stop(id: string) {
        try {
          const path = generatePath(URL.CYCLE_STOP, { id });
          await http.post(path);
          toast.success('Cycle stopped successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async pause(id: string) {
        try {
          const path = generatePath(URL.CYCLE_PAUSE, { id });
          await http.post(path);
          toast.success('Cycle paused successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async unpause(id: string) {
        try {
          const path = generatePath(URL.CYCLE_UNPAUSE, { id });
          await http.post(path);
          toast.success('Cycle unpaused successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async updateSettings(data: { id: string; payload: { action: CycleSettingAction; value?: string } }) {
        try {
          const path = generatePath(URL.CYCLE_SETTINGS, { id: data.id });
          await http.put(path, data.payload);
          toast.success('The cycle settings updated');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async updateDbSettings(data, rootState) {
        try {
          const { id, payload } = data;

          const hasPending = rootState.cycles.cycleDbSettings?.pendingCycleSettings != null;

          const updatedPayload = hasPending ? { ...payload, clearPendingSettings: true } : payload;

          const path = generatePath(URL.CYCLE_DB_SETTINGS, { id });
          await http.put(path, updatedPayload);

          await this.getDbCycleSettings(id);

          toast.success('Cycle settings updated successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getMetaFields(data: { id: string }): Promise<MetaField[]> {
        try {
          const path = generatePath(URL.CYCLE_META_FIELDS, { id: data.id });
          const response = await http.get(path);
          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },
      async addMetaField(data: { id: string; metaField: MetaField }): Promise<MetaField> {
        try {
          const path = generatePath(URL.ADD_CYCLE_META_FIELDS, { id: data.id });
          const response = await http.put(path, { metaField: data.metaField });
          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async updateMetaFields({ id, updatedFields }: { id: string; updatedFields: MetaField[] }) {
        try {
          const path = generatePath(URL.UPDATE_CYCLE_META_FIELDS, { id });
          const response = await http.put(path, { updatedFields });

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
        }
      },

      async removeMetaField(data: { id: string; key: string }) {
        try {
          const path = generatePath(URL.REMOVE_CYCLE_META_FIELDS, {
            id: data.id,
          });
          await http.delete(path, { data: { key: data.key } });
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async saveAsDefault(data: { id: string }) {
        try {
          const path = generatePath(URL.SAVE_META_FIELDS_AS_DEFAULT, { id: data.id });
          const response = await http.put(path);

          if (response.status === 200) {
            toast.success('The cycle meta fields saved as default');
          }
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      },

      async loadDefaultTags(data: { id: string }) {
        try {
          const path = generatePath(URL.LOAD_DEFAULT_TAGS, { id: data.id });
          const response = await http.post(path);
          if (response.status === 201) {
            toast.success('The default meta tags loaded successfully');
          }

          return response.data;
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      },

      async saveAttachment({ id, attachment }: { id: string; attachment: File }) {
        try {
          const path = generatePath(URL.CYCLE_ATTACHMENTS, { id });
          const formData = new FormData();
          formData.append('attachment', attachment);

          const response = await http.post(path, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          dispatch.cycles.setAttachments(response.data);
          if (response.status === 200) {
            dispatch.cycles.setAttachments(response.data);
          }
          toast.success('The attachment saved successfully');
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
        }
      },

      async getAttachments(id: string) {
        try {
          const path = generatePath(URL.CYCLE_ATTACHMENTS, { id });
          const response = await http.get(path);

          if (response.status === 200) {
            dispatch.cycles.setAttachments(response.data);
          }
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      },

      async deleteAttachment({ cycleId, attachmentId }: { cycleId: string; attachmentId: string }, state) {
        try {
          const path = generatePath(URL.CYCLE_ATTACHMENT, { id: cycleId, attachmentId });
          const response = await http.delete(path);

          if (response.status === 200) {
            toast.success('The file successfully deleted');

            const filteredAttachments = state.cycles.attachments.filter((attachment) => attachment.id !== attachmentId);
            dispatch.cycles.setAttachments(filteredAttachments);
          }
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      },

      async savePackage({ id, cyclePackage }: { id: string; cyclePackage: File }, state) {
        try {
          const path = generatePath(URL.CYCLE_PACKAGES, { id });
          const formData = new FormData();
          formData.append('cyclePackage', cyclePackage);

          const response = await http.post(path, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (response.status === 201) {
            dispatch.cycles.setCyclePackage([...state.cycles.packages, response.data]);
          }
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      },
      async getPackage(id: string) {
        try {
          const path = generatePath(URL.CYCLE_PACKAGES, { id });
          const response = await http.get(path);
          if (response.status === 200 && response.data) {
            dispatch.cycles.setCyclePackage(response.data);
          } else {
            dispatch.cycles.setCyclePackage(null);
          }
        } catch (error: unknown) {
          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          }
        }
      },

      async getExcludeChannelNames(cycleId: string) {
        try {
          raiseCondtitionCheck.excludeChannelNames += 1;

          const requestNum = raiseCondtitionCheck.excludeChannelNames;

          const response = await http.get<IExcludedChannelNames[]>(
            generatePath(URL.CYCLE_EXCLUDE_CHANNEL_NAMES, { id: cycleId }),
          );

          const excludedNames = response.data;

          if (requestNum === raiseCondtitionCheck.excludeChannelNames) {
            dispatch.cycles.setExcludedChannelsNames(excludedNames);
          }

          return excludedNames;
        } catch (error) {
          dispatch.reports.setError(error.message);
        }
      },

      async updateExcludeChannelNames({ cycleId, data }: TUpdateExcludeChannelNames) {
        try {
          const path = generatePath(URL.CYCLE_EXCLUDE_CHANNEL_NAMES, { id: cycleId });
          const response = await http.put(path, data);
          return response.data;
        } catch (error) {
          dispatch.reports.setError(error.message);
        }
      },

      async getColorDevices(cycleId: string) {
        try {
          raiseCondtitionCheck.colorDevices += 1;

          const requestNum = raiseCondtitionCheck.colorDevices;
          const response = await http.get<IColorsDevices[]>(generatePath(URL.CYCLE_DEVICE_COLORS, { id: cycleId }));

          const colorDevices = response.data;

          if (requestNum === raiseCondtitionCheck.colorDevices) {
            dispatch.cycles.setColorDevices(colorDevices);
          }

          return colorDevices;
        } catch (error) {
          dispatch.reports.setError(error.message);
        }
      },

      async updateDeviceColorsReport({ cycleId, data }: TUpdateColorDevices) {
        try {
          const path = generatePath(URL.CYCLE_DEVICE_COLORS, { id: cycleId });
          const response = await http.put(path, data);
          return response.data;
        } catch (error) {
          dispatch.reports.setError(error.message);
        }
      },
    };
  },
});
